import React, { useEffect, useMemo, useState } from "react";
import { apiGetRequest } from "helpers/Requests";
import { URL_ROSTERING_SHIFT_END_ANSWERS } from "store/services/URL";
import moment from "moment";
import { parseBooleanVal } from "Utils/utils";
import {
  TimesheetTabSectionHeader,
  TimesheetStatusChip,
} from "../../../components/TimesheetRevamp/TimesheetTabPrimitives";
import { TimesheetQuestionsTabSkeleton } from "../../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";

const ShiftEndQuestionList = ({ uuid }) => {
  const [loader, setLoader] = useState(false);
  const [shiftEndAnswers, setShiftEndAnswers] = useState([]);
  const endPoint = `${URL_ROSTERING_SHIFT_END_ANSWERS}${uuid}`;

  useEffect(() => {
    const fetchShiftEndAnswers = async () => {
      try {
        setLoader(true);
        const response = await apiGetRequest(endPoint);
        if (response?.status === 200) {
          setShiftEndAnswers(response?.data?.data?.data || []);
        }
      } catch (error) {
        // silent
      } finally {
        setLoader(false);
      }
    };
    if (uuid) {
      fetchShiftEndAnswers();
    }
  }, [uuid, endPoint]);

  const groupedShift =
    Array?.isArray(shiftEndAnswers) &&
    shiftEndAnswers?.length > 0 &&
    shiftEndAnswers[0]?.questions;

  const toTitleCase = (str) =>
    str
      ? str
          .split(" ")
          .map(
            (word) => word?.charAt(0).toUpperCase() + word?.slice(1).toLowerCase()
          )
          .join(" ")
      : "";

  const formatAnswer = (answer) => {
    if (!answer) return null;
    return answer.charAt(0).toUpperCase() + answer.slice(1).toLowerCase();
  };

  const participantSections = useMemo(() => {
    if (groupedShift) {
      return shiftEndAnswers.map((participant, index) => ({
        key: participant?.pt_id || index,
        title: toTitleCase(participant?.participant),
        questions: participant?.questions || [],
      }));
    }

    if (shiftEndAnswers?.length > 0) {
      return [
        {
          key: "flat",
          title: null,
          questions: shiftEndAnswers,
        },
      ];
    }

    return [];
  }, [groupedShift, shiftEndAnswers]);

  if (loader) {
    return (
      <div className="timesheet-tab-content min-h-0">
        <TimesheetQuestionsTabSkeleton />
      </div>
    );
  }

  if (!participantSections.length) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-dashed border-[#E5E7EB] bg-[#F8FAFC] py-12 text-sm text-[#94A3B8]">
        No shift end questions found.
      </div>
    );
  }

  return (
    <div className="timesheet-tab-content space-y-3">
      <TimesheetTabSectionHeader
        title="Shift end questions"
        subtitle="Answers captured when the worker finished this shift"
      />

      {participantSections.map((section) => {
        const answeredCount = section.questions.filter((q) => q?.answer).length;
        const skippedCount = section.questions.length - answeredCount;

        return (
          <div key={section.key} className="space-y-3">
            {section.title && (
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="text-[13px] font-bold text-[#0F172A]">
                  {section.title}
                </p>
                {section.questions.length > 0 && (
                  <TimesheetStatusChip tone="approve">
                    {answeredCount} answered · {skippedCount} skipped
                  </TimesheetStatusChip>
                )}
              </div>
            )}

            {section.questions.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">
                No shift end questions found for this participant.
              </p>
            ) : (
              <div className="space-y-2">
                {section.questions.map((item, index) => {
                  const hasAnswer = Boolean(item?.answer);
                  const isMandatory = parseBooleanVal(item?.is_mandatory);
                  const questionText =
                    item?.question?.charAt(0).toUpperCase() +
                    item?.question?.slice(1).toLowerCase();

                  return (
                    <div
                      key={`${section.key}-${index}`}
                      className={`td-question-card ${
                        !hasAnswer ? "is-missed" : ""
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-start gap-2">
                          <TimesheetStatusChip tone="brand">
                            Q{index + 1}
                          </TimesheetStatusChip>
                          <p className="text-[12.5px] font-semibold text-[#0F172A]">
                            {questionText}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {isMandatory && (
                            <TimesheetStatusChip tone="pending">
                              Mandatory
                            </TimesheetStatusChip>
                          )}
                          {!hasAnswer && (
                            <TimesheetStatusChip tone="pending">
                              No answer
                            </TimesheetStatusChip>
                          )}
                          {item?.created_at && (
                            <span className="text-[11px] font-medium text-[#94A3B8]">
                              {moment(item.created_at).format("DD/MM/YYYY")}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg bg-[#F8FAFC] px-3 py-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#94A3B8]">
                          Answer
                        </p>
                        <p
                          className={`mt-1 text-[13px] leading-relaxed ${
                            hasAnswer ? "text-[#0F172A]" : "text-[#94A3B8]"
                          }`}
                        >
                          {hasAnswer
                            ? formatAnswer(item.answer)
                            : "No answer provided"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ShiftEndQuestionList;
